import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { customersApi } from '@/features/customers/api'
import { getErrorMessage } from '@/features/shared'
import { queryKeys } from '@/lib/query-keys'

import type { QueryClient } from '@tanstack/react-query'
import type { Customer } from '@/types'

/** Forget every remembered "archived customers" lookup (the Archived view and
 *  the "N archived customers match" hint). Call after anything that changes
 *  who is archived — archive, restore, merge, erase — so a stale count is never
 *  shown again (it used to say "1 archived customer matches" after that
 *  customer was erased). The next look fetches fresh. */
export function dropArchivedLookups(queryClient: QueryClient): void {
  queryClient.removeQueries({
    queryKey: queryKeys.customers(),
    predicate: (q) => {
      const params = q.queryKey[1]
      return typeof params === 'object' && params !== null && 'archived' in params
    },
  })
}

/* Archive and restore for the customers directory.

   "Archive" is the backend's soft delete: the customer leaves the directory
   and their online (storefront) login stops working, but nothing is lost —
   orders, points and purchase-limit history stay on the record, and Restore
   brings all of it back. It used to be labelled "Delete … cannot be undone",
   which was neither true nor what staff needed to know. */

interface UseCustomerArchiveOptions {
  /** After a single archive succeeds (close the panel, clear the dialog). */
  onArchived?: () => void
  /** After a bulk archive succeeds (clear the selection, close the dialog). */
  onBulkArchived?: () => void
  /** After a restore succeeds — the customer is active again, so the caller
   *  takes staff to them in the directory (the Archived view no longer lists them). */
  onRestored?: (customer: Customer) => void
}

export function useCustomerArchive({ onArchived, onBulkArchived, onRestored }: UseCustomerArchiveOptions = {}) {
  const queryClient = useQueryClient()
  const refreshDirectory = () => queryClient.invalidateQueries({ queryKey: queryKeys.customers() })

  const archive = useMutation({
    mutationFn: (id: string) => customersApi.customers.delete(id),
    onSuccess: (_data, id) => {
      toast.success('Customer archived')
      // Their cached profile is now out of date (they're archived) — drop it so
      // nothing keeps showing the active panel for them.
      queryClient.removeQueries({ queryKey: queryKeys.customer(id) })
      dropArchivedLookups(queryClient)
      onArchived?.()
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to archive customer')),
    onSettled: () => { void refreshDirectory() },
  })

  const bulkArchive = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => customersApi.customers.delete(id))),
    onSuccess: (_data, ids) => {
      toast.success('Selected customers archived')
      for (const id of ids) queryClient.removeQueries({ queryKey: queryKeys.customer(id) })
      dropArchivedLookups(queryClient)
      onBulkArchived?.()
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to archive some customers')),
    onSettled: () => { void refreshDirectory() },
  })

  const restore = useMutation({
    mutationFn: (id: string) => customersApi.customers.restore(id),
    onSuccess: (customer) => {
      toast.success(`${customer.firstName} ${customer.lastName} restored`)
      dropArchivedLookups(queryClient)
      onRestored?.(customer)
    },
    // The server explains a refused restore (merged, erased, email now in use).
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to restore customer')),
    onSettled: (_data, _error, id) => {
      void refreshDirectory()
      void queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
    },
  })

  return { archive, bulkArchive, restore }
}
